import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiPlus, FiX } from 'react-icons/fi';
import { fetchUsers, updateUserRoles, createUser, deleteUser, resetUserPassword } from '../../../services/api';
import './UserEditor.css';
import { Role, RoleOption, asSettableRoles, roleLabel, settableRoleOptions } from '../../../model/Roles';
import { useMessages } from '../../../i18n/I18n';
import Modal from '../../../components/Modal';
import PageHeader from '../../../components/PageHeader';
import LoadingPlaceholder from '../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../services/tabData';

interface User {
  user_id: string;
  email: string;
  roles: string[];
}

interface UserEditorProps {
  sessionToken: string;
  festivalId: string;
  currentUser: User;
}

interface DropdownProps {
  options: RoleOption[];
  value: string[];
  onChange: (newValue: string[]) => void;
  disabled?: boolean;
}

const RolesDropdown: React.FC<DropdownProps> = ({ options, value, onChange, disabled }) => {
  const t = useMessages('users');
  const roles = useMessages('roles');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleRole = (roleValue: string) => {
    let newRoles: string[];
    
    if (roleValue === Role.ADMIN) {
      // If selecting admin, clear all other roles
      newRoles = value.includes(Role.ADMIN) ? [] : [Role.ADMIN];
    } else {
      // If selecting another role, remove admin if present and toggle the selected role
      newRoles = value.filter(r => r !== Role.ADMIN);
      if (value.includes(roleValue)) {
        newRoles = newRoles.filter(r => r !== roleValue);
      } else {
        newRoles.push(roleValue);
      }
    }
    
    onChange(newRoles);
  };

  return (
    <div className={`roles-dropdown${isOpen ? ' open' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-header ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-expanded={isOpen}
      >
        <span className="selected-roles">
          {value.length === 0 ? (
            <span className="placeholder">{t.selectRoles}</span>
          ) : (
            <span className="role-tags">
              {value.map(role => (
                <span key={role} className="role-tag">
                  {roleLabel(role, roles)}
                </span>
              ))}
            </span>
          )}
        </span>
        <FiChevronDown className="dropdown-arrow" aria-hidden="true" />
      </button>
      {isOpen && !disabled && (
        <div className="dropdown-options">
          {options.map(option => (
            <label key={option.value} className="dropdown-option">
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => toggleRole(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

interface NewUserModalProps {
  onClose: () => void;
  onSubmit: (email: string, roles: string[]) => Promise<void>;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const NewUserModal: React.FC<NewUserModalProps> = ({ onClose, onSubmit }) => {
  const t = useMessages('users');
  const common = useMessages('common');
  const roleOptions = settableRoleOptions(useMessages('roles'));
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: t.emailRequired,
    roles: t.rolesRequired
  });
  /** The errors show once the form has been filled in a little, not before */
  const [hasStarted, setHasStarted] = useState(false);

  const validateEmail = (email: string): string => {
    if (!email) return t.emailRequired;
    if (!isValidEmail(email)) return t.emailInvalid;
    return '';
  };

  const validateRoles = (roles: string[]): string => {
    if (roles.length === 0) return t.rolesRequired;
    return '';
  };

  const validateForm = (): boolean => {
    const emailError = validateEmail(email);
    const rolesError = validateRoles(selectedRoles);

    setValidationErrors({
      email: emailError,
      roles: rolesError
    });

    return !emailError && !rolesError;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setHasStarted(true);
    setValidationErrors(prev => ({
      ...prev,
      email: validateEmail(newEmail)
    }));
  };

  const handleRolesChange = (newRoles: string[]) => {
    setSelectedRoles(newRoles);
    setHasStarted(true);
    setValidationErrors(prev => ({
      ...prev,
      roles: validateRoles(newRoles)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit(email, selectedRoles);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.createFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = !validationErrors.email && !validationErrors.roles;

  return (
    <div className="modal-overlay" onClick={() => !isSubmitting && onClose()}>
      <div className="modal-content users-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t.newUser}</h3>
          {!isSubmitting && (
            <button type="button" className="btn btn-ghost btn-icon dialog-close" onClick={onClose} aria-label={common.close}>
              <FiX aria-hidden="true" />
            </button>
          )}
        </div>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-form-group">
            <label htmlFor="email">{t.email}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              className={validationErrors.email ? 'error' : ''}
              required
              disabled={isSubmitting}
            />
            {hasStarted && validationErrors.email && (
              <div className="input-error">{validationErrors.email}</div>
            )}
          </div>
          <div className="modal-form-group">
            <label>{t.roles}</label>
            <RolesDropdown
              options={roleOptions}
              value={selectedRoles}
              onChange={handleRolesChange}
              disabled={isSubmitting}
            />
            {hasStarted && validationErrors.roles && (
              <div className="input-error">{validationErrors.roles}</div>
            )}
          </div>
          <div className="modal-actions">
            <button 
              type="submit" 
              disabled={isSubmitting || !isFormValid}
            >
              {isSubmitting ? (
                <span className="button-content">
                  <span className="spinner"></span>
                  {t.creating}
                </span>
              ) : (
                t.createUser
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              {common.cancel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteConfirmModalProps {
  user: User;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ user, onConfirm, onCancel }) => {
  const t = useMessages('users');
  const common = useMessages('common');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm();
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.deleteFailed);
      setIsDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !isDeleting && onCancel()}>
      <div className="modal-content users-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t.deleteTitle}</h3>
          {!isDeleting && (
            <button type="button" className="btn btn-ghost btn-icon dialog-close" onClick={onCancel} aria-label={common.close}>
              <FiX aria-hidden="true" />
            </button>
          )}
        </div>
        {error && <div className="modal-error">{error}</div>}
        <p>{t.deleteConfirm(user.email)}</p>
        <div className="modal-actions">
          <button 
            className="danger"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <span className="button-content">
                <span className="spinner"></span>
                {t.deleting}
              </span>
            ) : (
              common.delete
            )}
          </button>
          <button 
            onClick={onCancel}
            disabled={isDeleting}
          >
            {common.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ResetPasswordModalProps {
  user: User;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ user, onConfirm, onCancel }) => {
  const t = useMessages('users');
  const common = useMessages('common');
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReset = async () => {
    setIsResetting(true);
    setError(null);
    try {
      await onConfirm();
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.resetFailed);
      setIsResetting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !isResetting && onCancel()} className="users-dialog">
      <div className="modal-header">
        <h3>{t.resetPassword}</h3>
        {!isResetting && (
          <button type="button" className="btn btn-ghost btn-icon dialog-close" onClick={onCancel} aria-label={common.close}>
            <FiX aria-hidden="true" />
          </button>
        )}
      </div>
      {error && <div className="modal-error">{error}</div>}
      <p>{t.resetConfirm(user.email)}</p>
      <div className="modal-actions">
        <button 
          onClick={handleReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <span className="button-content">
              <span className="spinner"></span>
              {t.resetting}
            </span>
          ) : (
            t.resetPassword
          )}
        </button>
        <button 
          onClick={onCancel}
          disabled={isResetting}
        >
          {common.cancel}
        </button>
      </div>
    </Modal>
  );
};

const UserEditor: React.FC<UserEditorProps> = ({ sessionToken, festivalId, currentUser }) => {
  const t = useMessages('users');
  const common = useMessages('common');
  const rolesMessages = useMessages('roles');
  const roleOptions = settableRoleOptions(rolesMessages);
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingRoles, setEditingRoles] = useState<{ [key: string]: string[] }>({});
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);

  useEffect(() => {
    loadUsers();
  }, [sessionToken, festivalId]);

  const loadUsers = async () => {
    try {
      const fetchedUsers = await loadTabData(festivalId, 'users', () => fetchUsers(sessionToken, festivalId));
      setUsers(fetchedUsers);
      const initialEditingRoles: { [key: string]: string[] } = {};
      fetchedUsers.forEach(user => {
        initialEditingRoles[user.user_id] = asSettableRoles(user.roles || []);
      });
      setEditingRoles(initialEditingRoles);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRoles = async (userId: string) => {
    setUpdatingUser(userId);
    try {
      await updateUserRoles(sessionToken, festivalId, userId, editingRoles[userId]);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.updateFailed);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleCreateUser = async (email: string, roles: string[]) => {
    await createUser(sessionToken, festivalId, email, roles);
    await loadUsers(); // Refresh the users list
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await deleteUser(sessionToken, festivalId, userId);
      await loadUsers(); // Refresh the users list
    } catch (err) {
      setError(err instanceof Error ? err.message : t.deleteFailed);
    }
  };

  const handleResetPassword = async (userId: string) => {
    try {
      await resetUserPassword(sessionToken, festivalId, userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.resetFailed);
    }
  };

  const isRolesValid = (roles: string[]): boolean => {
    return roles.length > 0;
  };

  if (loading) {
    return (
      <div className="user-editor">
        <PageHeader title={t.title} description={t.description} />
        <LoadingPlaceholder layout="table" count={4} />
      </div>
    );
  }

  if (error) {
    return <div className="user-editor-error">{error}</div>;
  }

  return (
    <div className="user-editor">
      <PageHeader
        title={t.title}
        count={users.length}
        description={t.description}
      >
        <button type="button" className="btn btn-primary" onClick={() => setShowNewUserModal(true)}>
          <FiPlus aria-hidden="true" />
          {t.newUser}
        </button>
      </PageHeader>
      <div className="data-table">
        <div className="users-table" role="table" aria-label={t.title}>
          <div className="data-table-head" role="row">
            <span role="columnheader">{t.email}</span>
            <span role="columnheader">{t.roles}</span>
            <span role="columnheader" className="users-actions-head">{t.actions}</span>
          </div>
          {users.map((user) => (
            <div
              key={user.user_id}
              role="row"
              className={`data-table-row${user.user_id === currentUser.user_id ? ' current-user' : ''}`}
            >
              <div role="cell" className="user-email">{user.email}</div>
              <div role="cell">
                {user.user_id !== currentUser.user_id ? (
                  <div className="role-editor">
                    <RolesDropdown
                      options={roleOptions}
                      value={editingRoles[user.user_id] || []}
                      onChange={(newRoles) => setEditingRoles(prev => ({
                        ...prev,
                        [user.user_id]: newRoles
                      }))}
                    />
                    <button
                      type="button"
                      className="btn update-roles-button"
                      onClick={() => handleUpdateRoles(user.user_id)}
                      disabled={
                        updatingUser === user.user_id || 
                        !isRolesValid(editingRoles[user.user_id] || [])
                      }
                    >
                      {updatingUser === user.user_id ? t.updating : t.updateRoles}
                    </button>
                  </div>
                ) : (
                  <div className="current-user-roles">
                    {user.roles ? asSettableRoles(user.roles).map((role) => (
                      <span key={role} className="current-user-role">
                        {roleLabel(role, rolesMessages)}
                      </span>
                    )) : <span className="current-user-role">{t.noRoles}</span>}
                  </div>
                )}
              </div>
              <div role="cell" className="user-actions-cell">
                {user.user_id === currentUser.user_id ? (
                  <span className="no-actions">{t.noActions}</span>
                ) : (
                  <div className="user-actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setUserToResetPassword(user)}
                    >
                      {t.resetPasswordAction}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => setUserToDelete(user)}
                    >
                      {common.delete}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {showNewUserModal && (
        <NewUserModal
          onClose={() => setShowNewUserModal(false)}
          onSubmit={handleCreateUser}
        />
      )}
      {userToDelete && (
        <DeleteConfirmModal
          user={userToDelete}
          onConfirm={() => handleDeleteUser(userToDelete.user_id)}
          onCancel={() => setUserToDelete(null)}
        />
      )}
      {userToResetPassword && (
        <ResetPasswordModal
          user={userToResetPassword}
          onConfirm={() => handleResetPassword(userToResetPassword.user_id)}
          onCancel={() => setUserToResetPassword(null)}
        />
      )}
    </div>
  );
};

export default UserEditor; 